import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Card, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import Layout from '../../components/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../constants';

const BestShelters = () => {
  const [numberOfRefugees, setNumberOfRefugees] = useState('');
  const [numberOfShelters, setNumberOfShelters] = useState('');
  const [topShelters, setTopShelters] = useState([]);
  const [allShelters, setAllShelters] = useState([]);
  const [selectedShelters, setSelectedShelters] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch all shelters
  useEffect(() => {
    const fetchShelters = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/shelters`);
        setAllShelters(response.data);
      } catch (error) {
        console.error('Error fetching shelters:', error);
      }
    };
    fetchShelters();
  }, []);

  // Notifications
  const showNotification = (type, message) => {
    setNotifications(prev => [...prev, { type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.message !== message));
    }, 10000);
  };

  // Handle form submit
const handleSubmit = async (e) => {
  e.preventDefault();
  setLoadingSubmit(true);

  try {
    const requestData = {
      numberOfRefugees: parseInt(numberOfRefugees),
      numberOfShelter: parseInt(numberOfShelters),
      shelterNames: selectedShelters.length > 0 ? selectedShelters : [],
    };

    const response = await axios.post(`${BASE_URL}/shelters/recommend`, requestData);
    const { allocation, totalAssigned, unassignedRefugees, message } = response.data;

    setTopShelters(allocation);

    if (unassignedRefugees && unassignedRefugees > 0) {
      showNotification('error', `${message} Assigned: ${totalAssigned}, Unassigned: ${unassignedRefugees}`);
    } else {
      showNotification('success', `All ${totalAssigned} refugees allocated successfully.`);
    }

    setSubmitted(true);
  } catch (error) {
    // ✅ Show backend message if it exists
    const backendMessage = error.response?.data?.message;
    showNotification('error', backendMessage || 'Failed to fetch recommended shelters.');
    console.error('Error fetching recommended shelters:', error);
  } finally {
    setLoadingSubmit(false);
  }
};



  // Validate numberOfShelters input
  const handleShelterChange = (e) => {
    const num = parseInt(e.target.value, 10);
    if (!isNaN(num) && num >= 1 && num <= allShelters.length) {
      setNumberOfShelters(num);
    } else {
      setNumberOfShelters('');
    }
  };

  return (
    <Layout>
      <div className="container py-4">
        <h4 className="mb-4">Best Shelters Recommendation</h4>

        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSubmit} className="p-4 rounded shadow-sm">
              <Row className="g-4">
                {/* Number of Refugees */}
                <Col md={6}>
                  <Form.Group controlId="numberOfRefugees">
                    <Form.Label>Number of Refugees</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter number of refugees"
                      value={numberOfRefugees}
                      onChange={(e) => setNumberOfRefugees(e.target.value)}
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Number of Shelters */}
                <Col md={6}>
                  <Form.Group controlId="numberOfShelters">
                    <Form.Label>Number of Shelters</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder={`Max: ${allShelters.length}`}
                      value={numberOfShelters}
                      onChange={handleShelterChange}
                      min="1"
                      max={allShelters.length}
                    />
                  </Form.Group>
                </Col>

                {/* Select Specific Shelters */}
                <Col md={12}>
                  <Form.Group controlId="selectedShelters">
                    <Form.Label>Select Specific Shelters</Form.Label>
                    <div className="d-flex flex-wrap gap-3 p-3 bg-white border rounded" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      {allShelters.map(s => (
                        <Form.Check
                          key={s._id}
                          type="checkbox"
                          label={s.name}
                          value={s.name}
                          checked={selectedShelters.includes(s.name)}
                          onChange={(e) => {
                            const { checked, value } = e.target;
                            setSelectedShelters(prev => checked ? [...prev, value] : prev.filter(n => n !== value));
                          }}
                          className="me-4"
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-4">
                <Button type="submit" variant="primary" disabled={loadingSubmit}>
                  {loadingSubmit ? <Spinner animation="border" size="sm" /> : "Submit"}
                </Button>
              </div>
            </Form>

            {/* Notifications */}
            {notifications.map((n, idx) => (
              <Alert key={idx} variant={n.type === 'error' ? 'danger' : 'success'} className="mt-3">
                {n.message}
              </Alert>
            ))}
          </Card.Body>
        </Card>

        {/* Recommended Shelters Table */}
        {submitted && topShelters.length > 0 && (
          <Card className="mb-4">
            <Card.Body>
              <h5>Top Recommended Shelters</h5>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    {topShelters.map((s, i) => <th key={i}>{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Assigned Refugees</td>
                    {topShelters.map((s, i) => <td key={i}>{s.assignedRefugees}</td>)}
                  </tr>
                  <tr>
                    <td>Capacity</td>
                    {topShelters.map((s, i) => <td key={i}>{s.capacity}</td>)}
                  </tr>
                  <tr>
                    <td>Available Places</td>
                    {topShelters.map((s, i) => <td key={i}>{s.totalFreePlaces}</td>)}
                  </tr>
                  <tr>
                    <td>Current Occupants</td>
                    {topShelters.map((s, i) => <td key={i}>{s.currentOccupants}</td>)}
                  </tr>
                  <tr>
                    <td>State Covered Users</td>
                    {topShelters.map((s, i) => <td key={i}>{s.stateCoveredUsers}</td>)}
                  </tr>
                  <tr>
                    <td>State Coverage (%)</td>
                    {topShelters.map((s, i) => <td key={i}>{s.stateCoverage}%</td>)}
                  </tr>
                  <tr>
                    <td>Cost per Occupant (€)</td>
                    {topShelters.map((s, i) => <td key={i}>{s.costPerOccupant}</td>)}
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default BestShelters;
